/**
 * FinancialsModal — captures the 15 institution-only financial fields for a
 * student. Rendered in the Institution dashboard when advancing stages, and in
 * the Admin dashboard as a read/edit tool.
 *
 * Props:
 *   - open (bool)
 *   - onOpenChange (fn)
 *   - student ({ id, full_name, sale_price, coe_sent_at, ... })
 *   - mode: 'advance-coe-sent' | 'advance-half-money' | 'advance-second-money' | 'edit'
 *       - advance-coe-sent    → POST /institution/students/{id}/mark-coe-sent
 *           captures the full financial snapshot at COE issuance.
 *       - advance-half-money  → POST /institution/students/{id}/mark-half-money
 *       - advance-second-money→ POST /institution/students/{id}/mark-second-money-received
 *       - edit                → PUT  /students/{id}/financials (no stage change)
 *   - onSaved (fn) — callback with the new financials & new stage (if any)
 *   - viewOnly (bool) — admin/institution can only view (no writes) when true
 */
import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { CurrencyDollar, CircleNotch, ChatCircleDots } from '@phosphor-icons/react';
import { API } from '@/config/api';

// Fields whose values are AUTO-COMPUTED from other fields — the president
// can never edit them directly. These follow the college's official formula:
//   Amount Received     = 1st Installment + Reg Fee
//   Amount Shared       = 1st Installment
//   1st School Portion  = 60% of School Value
//   1st Invoice Value   = 1st Installment - 1st School Portion
//   2nd School Portion  = School Value - 1st School Portion
//   2nd Invoice Value   = 2nd Installment - 2nd School Portion
const COMPUTED_FIELDS = new Set([
  'amount_received',
  'amount_shared',
  'first_school_portion',
  'first_invoice_value',
  'second_school_portion',
  'second_invoice_value',
]);

const num = (v) => {
  if (v === '' || v === null || v === undefined) return 0;
  const n = Number(String(v).replace(/[^0-9.-]/g, ''));
  return Number.isFinite(n) ? n : 0;
};
const fmt2 = (n) => (Number.isFinite(n) ? Number(n.toFixed(2)) : 0).toString();

function computeDerived(form) {
  const reg      = num(form.reg_fee);
  const school   = num(form.school_value);
  const first    = num(form.first_installment);
  const second   = num(form.second_installment);
  const firstSchool  = school * 0.6;
  const secondSchool = school - firstSchool; // == 40%
  return {
    amount_received:       fmt2(first + reg),
    amount_shared:         fmt2(first),
    first_school_portion:  fmt2(firstSchool),
    first_invoice_value:   fmt2(first - firstSchool),
    second_school_portion: fmt2(secondSchool),
    second_invoice_value:  fmt2(second - secondSchool),
  };
}

// Column order copied straight from the user's spreadsheet screenshot.
// Grouped for form layout, but the underlying keys match the backend model.
const FIELD_GROUPS = [
  {
    heading: 'Payment received',
    fields: [
      { key: 'date_coe_done',                label: 'Date COE Done',           type: 'date' },
      // Both tranche dates live in the same group. `FinancialsModal`'s
      // render layer picks the right one to show based on `mode`:
      //   • advance-half-money   → show `date_received_money` only, labeled
      //                             "Date Received 1/2 Money"
      //   • advance-second-money → show `date_received_second_money` only,
      //                             labeled "Date Received 2/2 Money"
      //   • advance-coe-sent     → show `date_received_money` only, greyed
      //                             out (money hasn't arrived yet)
      //   • edit / view          → show BOTH so admins can audit the full
      //                             ledger of what came in when.
      { key: 'date_received_money',          label: 'Date Received 1/2 Money', type: 'date' },
      { key: 'date_received_second_money',   label: 'Date Received 2/2 Money', type: 'date' },
      { key: 'amount_received',              label: 'Amount Received',         type: 'currency' },
      { key: 'reg_fee',                      label: 'Reg Fee',                 type: 'currency' },
      { key: 'amount_shared',                label: 'Amount Shared',           type: 'currency' },
    ],
  },
  {
    heading: 'Contract',
    fields: [
      { key: 'contract_value',       label: 'Contract Value',      type: 'currency' },
      { key: 'school_value',         label: 'School Value',        type: 'currency' },
    ],
  },
  {
    heading: '1st Installment',
    fields: [
      { key: 'first_installment',        label: '1st Installment',       type: 'currency' },
      { key: 'first_school_portion',     label: '1st School Portion',    type: 'currency' },
      { key: 'first_invoice_value',      label: '1st Invoice Value',     type: 'currency' },
    ],
  },
  {
    heading: '2nd Installment',
    fields: [
      { key: 'second_installment',       label: '2nd Installment',       type: 'currency' },
      { key: 'second_school_portion',    label: '2nd School Portion',    type: 'currency' },
      { key: 'second_invoice_value',     label: '2nd Invoice Value',     type: 'currency' },
    ],
  },
  {
    heading: 'Notes',
    fields: [
      { key: 'agent',                label: 'Agent',               type: 'text' },
      { key: 'comments',             label: 'Comments',            type: 'textarea', full: true },
    ],
  },
];

const EMPTY = FIELD_GROUPS.flatMap(g => g.fields).reduce((acc, f) => ({ ...acc, [f.key]: '' }), {});

export default function FinancialsModal({ open, onOpenChange, student, mode = 'edit', onSaved, viewOnly = false }) {
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [meta, setMeta] = useState({ updated_at: '', updated_by: '' });

  useEffect(() => {
    if (!open || !student?.id) return;
    setLoading(true);
    axios.get(`${API}/students/${student.id}/financials`).then(({ data }) => {
      const next = { ...EMPTY };
      Object.keys(EMPTY).forEach(k => { next[k] = data[k] || ''; });
      // Auto-populate `date_coe_done` from the timestamp the president moved
      // the student to COE Sent — saves them from retyping a date the
      // system already knows.
      if (!next.date_coe_done && student.coe_sent_at) {
        try { next.date_coe_done = student.coe_sent_at.split('T')[0]; } catch { /* ignore */ }
      }
      // Advance-COE mode: prefill sensible defaults so the president only
      // has to review numbers and click Save. The 1st/2nd installment
      // amounts come straight from the EA the staff already sent (60/40
      // split of tuition frozen at `ea_prepared_data.quarter_1|2`) — no
      // point re-typing what the student already signed for.
      if (mode === 'advance-coe-sent') {
        if (!next.date_coe_done) next.date_coe_done = new Date().toISOString().slice(0, 10);
        if (!next.reg_fee)       next.reg_fee = '250';
        if (!next.school_value)  next.school_value = '5000';
        if (!next.contract_value && student.sale_price) next.contract_value = String(student.sale_price);
        // Autofill installments from the signed EA snapshot. Values are
        // stored as formatted money strings ("$9,300.00") — strip to a
        // plain decimal so the Input treats them correctly.
        const stripMoney = (s) => (s == null ? '' : String(s).replace(/[$,\s]/g, ''));
        const ea = student.ea_prepared_data || {};
        if (!next.first_installment  && ea.quarter_1) next.first_installment  = stripMoney(ea.quarter_1);
        if (!next.second_installment && ea.quarter_2) next.second_installment = stripMoney(ea.quarter_2);
        // Money hasn't actually arrived yet at this stage — the president
        // clicks "Mark 1/2 Money Received" LATER and fills the date then.
        // Wipe any stale value so the field renders as empty + disabled.
        next.date_received_money = '';
      }
      // Always recompute derived fields on load so stale values don't leak.
      setForm({ ...next, ...computeDerived(next) });
      setMeta({ updated_at: data.financials_updated_at || '', updated_by: data.financials_updated_by || '' });
    }).catch((err) => {
      // Recruiters get 403; that's fine — modal shouldn't be reachable for them.
      toast.error(err?.response?.data?.detail || 'Failed to load financials');
    }).finally(() => setLoading(false));
  }, [open, student?.id, student?.coe_sent_at, student?.sale_price, mode]);

  const setField = (k, v) => setForm(f => {
    // Never allow direct edits to computed columns.
    if (COMPUTED_FIELDS.has(k)) return f;
    const next = { ...f, [k]: v };
    return { ...next, ...computeDerived(next) };
  });

  const save = async () => {
    if (viewOnly) return;
    setSaving(true);
    try {
      if (mode === 'advance-coe-sent') {
        const { data } = await axios.post(`${API}/institution/students/${student.id}/mark-coe-sent`, form);
        toast.success('COE Sent · financial snapshot saved');
        onSaved?.({ financials: data.financials, new_stage: data.new_stage, financials_updated_at: data.financials_updated_at });
      } else if (mode === 'advance-half-money') {
        const { data } = await axios.post(`${API}/institution/students/${student.id}/mark-half-money`, form);
        toast.success('Financials saved · pipeline advanced to 1/2 Money Received');
        onSaved?.({ financials: data.financials, new_stage: data.new_stage, financials_updated_at: data.financials_updated_at });
      } else if (mode === 'advance-second-money') {
        const { data } = await axios.post(`${API}/institution/students/${student.id}/mark-second-money-received`, form);
        toast.success('Financials saved · pipeline advanced to 2/2 Money Received');
        onSaved?.({ financials: data.financials, new_stage: data.new_stage, financials_updated_at: data.financials_updated_at });
      } else {
        const { data } = await axios.put(`${API}/students/${student.id}/financials`, form);
        toast.success('Financials saved');
        onSaved?.({ financials: data.financials, financials_updated_at: data.financials_updated_at });
      }
      onOpenChange(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to save financials');
    } finally { setSaving(false); }
  };

  if (!student) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="financials-modal">
        <DialogHeader>
          <DialogTitle style={{ fontFamily: "'Libre Baskerville', serif" }} className="flex items-center gap-2">
            <CurrencyDollar size={20} weight="fill" className="text-emerald-600" />
            Financials — {student.full_name || student.id}
          </DialogTitle>
          <DialogDescription>
            {mode === 'advance-coe-sent'
              ? 'Enter the payment breakdown the college locks in when the COE is issued. Contract, installments and invoice values will feed the tranche-1 and tranche-2 commission flows.'
              : mode === 'advance-half-money'
              ? 'Enter the payment details the college has received. Saving will advance the pipeline to "1/2 Money Received".'
              : mode === 'advance-second-money'
              ? 'Enter the FINAL payment details (2nd installment + 2nd invoice value). Saving will advance the pipeline to "2/2 Money Received" and flag the admin to raise the final commission invoice.'
              : 'Update the financials the college recorded for this student. Recruiters cannot see this table.'}
          </DialogDescription>
          {meta.updated_at && (
            <p className="text-[10px] text-gray-500 mt-1">
              Last updated {new Date(meta.updated_at).toLocaleString()}{meta.updated_by ? ` · ${meta.updated_by}` : ''}
            </p>
          )}
        </DialogHeader>

        {loading ? (
          <div className="py-10 flex items-center justify-center text-gray-500 gap-2"><CircleNotch size={16} className="animate-spin" /> Loading…</div>
        ) : (
          <div className="space-y-6">
            {FIELD_GROUPS.filter(g => {
              // Gate field groups by mode so president can't accidentally
              // overwrite the wrong tranche's amounts while advancing a stage.
              // (advance-coe-sent shows EVERYTHING — it's the full snapshot.)
              if (mode === 'advance-half-money' && g.heading === '2nd Installment') return false;
              if (mode === 'advance-second-money' && g.heading === '1st Installment') return false;
              return true;
            }).map(group => (
              <fieldset key={group.heading} className="border border-gray-200 rounded-md p-3">
                <legend className="px-2 text-xs uppercase tracking-wider font-semibold text-gray-500">{group.heading}</legend>
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
                  {group.fields.filter(f => {
                    // Show the RIGHT tranche-date field for each advance
                    // flow — a president shouldn't see two "Date Received"
                    // rows on the same modal. In `edit` / `view` mode we
                    // show both so admins can audit the full ledger.
                    if (f.key === 'date_received_money' && mode === 'advance-second-money') return false;
                    if (f.key === 'date_received_second_money' && (mode === 'advance-coe-sent' || mode === 'advance-half-money')) return false;
                    return true;
                  }).map(f => {
                    const isComputed = COMPUTED_FIELDS.has(f.key);
                    // Grey out the "Date Received 1/2 Money" field on the
                    // Mark COE Sent flow — money literally hasn't been
                    // received yet, that's a later event. The president
                    // fills it in when they click "Mark 1/2 Money
                    // Received", at which point this field goes hot
                    // again (mode = 'advance-half-money' or 'edit').
                    const lockedForCoeSent = mode === 'advance-coe-sent' && f.key === 'date_received_money';
                    const disabled = viewOnly || isComputed || lockedForCoeSent;
                    return (
                    <div key={f.key} className={f.full ? 'sm:col-span-2 md:col-span-3' : ''}>
                      <Label className="text-xs text-gray-600 mb-1 block">
                        {f.label}
                        {isComputed && <span className="ml-1.5 text-[9px] font-semibold uppercase tracking-wider text-gray-400">Auto</span>}
                        {lockedForCoeSent && <span className="ml-1.5 text-[9px] font-semibold uppercase tracking-wider text-amber-600">Set on 1/2 Money Received</span>}
                      </Label>
                      {f.type === 'textarea' ? (
                        <Textarea
                          value={form[f.key]}
                          onChange={e => setField(f.key, e.target.value)}
                          disabled={disabled}
                          rows={3}
                          data-testid={`fin-${f.key}`}
                        />
                      ) : f.type === 'date' ? (
                        <Input type="date" value={form[f.key]} onChange={e => setField(f.key, e.target.value)} disabled={disabled} data-testid={`fin-${f.key}`} />
                      ) : f.type === 'currency' ? (
                        <div className="relative">
                          <span className="absolute left-2 top-1/2 -translate-y-1/2 text-xs text-gray-400">$</span>
                          <Input
                            type="text"
                            inputMode="decimal"
                            className={`pl-6 ${isComputed ? 'bg-gray-50 text-gray-700' : ''}`}
                            value={form[f.key]}
                            onChange={e => setField(f.key, e.target.value)}
                            placeholder="0.00"
                            disabled={disabled}
                            readOnly={isComputed}
                            data-testid={`fin-${f.key}`}
                          />
                        </div>
                      ) : (
                        <Input type="text" value={form[f.key]} onChange={e => setField(f.key, e.target.value)} disabled={disabled} data-testid={`fin-${f.key}`} />
                      )}
                    </div>
                  );})}
                </div>
              </fieldset>
            ))}
          </div>
        )}

        <div className="flex justify-end gap-2 pt-2 border-t border-gray-100 mt-4">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>Close</Button>
          {!viewOnly && (
            <Button onClick={save} className="bg-emerald-600 hover:bg-emerald-700 text-white" disabled={saving || loading} data-testid="financials-save-btn">
              {saving ? <><CircleNotch size={14} className="mr-1 animate-spin" /> Saving…</> : (mode === 'advance-coe-sent' ? 'Save & Mark COE Sent' : mode === 'advance-half-money' ? 'Save & Advance Stage' : mode === 'advance-second-money' ? 'Save & Mark 2/2 Received' : 'Save Financials')}
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

// Small read-only summary that fits inside a student card / detail panel.
export function FinancialsSummary({ studentId, canEdit, onEditClick }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!studentId) return;
    setLoading(true);
    axios.get(`${API}/students/${studentId}/financials`).then(({ data }) => setData(data)).catch(() => setData(null)).finally(() => setLoading(false));
  }, [studentId]);

  if (loading) return <div className="text-xs text-gray-400">Loading financials…</div>;
  if (!data) return null;

  const hasAny = Object.entries(data).some(([k, v]) => !['student_id', 'student_name', 'student_email', 'financials_updated_at', 'financials_updated_by'].includes(k) && v && String(v).trim());

  return (
    <div className="border border-emerald-200 bg-emerald-50/40 rounded-md p-3" data-testid={`financials-summary-${studentId}`}>
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-1.5 text-xs font-semibold text-emerald-800 uppercase tracking-wider">
          <CurrencyDollar size={14} weight="fill" /> Financials
        </div>
        {canEdit && (
          <Button size="sm" variant="ghost" onClick={onEditClick} className="h-6 px-2 text-[11px] text-emerald-700 hover:text-emerald-900" data-testid={`financials-edit-${studentId}`}>Edit</Button>
        )}
      </div>
      {!hasAny ? (
        <p className="text-[11px] text-gray-500 italic">No financials recorded yet.</p>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 text-[11px] text-gray-700">
          {[
            ['Date COE', data.date_coe_done],
            ['Date Money', data.date_received_money],
            ['Amount', data.amount_received],
            ['Reg Fee', data.reg_fee],
            ['Contract', data.contract_value],
            ['1st Instal.', data.first_installment],
            ['2nd Instal.', data.second_installment],
            ['Agent', data.agent],
          ].filter(([, v]) => v && String(v).trim()).map(([label, v]) => (
            <div key={label}>
              <p className="text-[9px] uppercase tracking-wide text-gray-500">{label}</p>
              <p className="font-semibold text-gray-900 truncate">{v}</p>
            </div>
          ))}
        </div>
      )}
      {data.comments && (
        <div className="mt-2 pt-2 border-t border-emerald-200/60 flex gap-1.5 text-[11px] text-gray-700">
          <ChatCircleDots size={12} className="mt-0.5 text-gray-400 shrink-0" />
          <span className="italic">{data.comments}</span>
        </div>
      )}
    </div>
  );
}
